(function () {
  /** @type {Window & { dataLayer?: Record<string, unknown>[]; __datalayerUserData?: { email?: string; phone_number?: string }; __pushDatalayerBeginCheckout?: () => Promise<void> }} */
  const win = window;

  /** @type {Record<string, unknown>[]} */
  const dataLayer = win.dataLayer ?? [];
  win.dataLayer = dataLayer;

  const CART_LINES_UPDATE_EVENT = 'shopify:cart:lines-update';
  const CART_DRAWER_OPEN_EVENT = 'theme-drawer:open';

  /** @typedef {{ customer_type: string; customer_id: string; user_data: { email?: string; phone_number?: string } }} CustomerContext */

  /** @type {{ shop_name: string; currency: string }} */
  let shopContext = {
    shop_name: '',
    currency: 'TRY',
  };

  /** @type {Promise<CustomerContext> | null} */
  let customerContextPromise = null;

  /**
   * @param {string} value
   * @returns {Promise<string>}
   */
  async function sha256Hex(value) {
    const normalized = value.trim();
    if (!normalized) return '';

    const encoded = new TextEncoder().encode(normalized);
    const digest = await crypto.subtle.digest('SHA-256', encoded);
    const bytes = Array.from(new Uint8Array(digest));
    return bytes.map((byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * @returns {Promise<CustomerContext>}
   */
  async function loadCustomerContext() {
    const node = document.getElementById('datalayer-customer-context');
    if (!(node instanceof HTMLScriptElement) || !node.textContent) {
      return {
        customer_type: 'Misafir',
        customer_id: '',
        user_data: {},
      };
    }

    try {
      const parsed = JSON.parse(node.textContent);
      /** @type {{ customer_type?: string; customer_id?: string; email?: string; phone_number?: string; shop_name?: string; currency?: string }} */
      const raw = parsed;

      if (typeof raw.shop_name === 'string') {
        shopContext.shop_name = raw.shop_name;
      }

      if (typeof raw.currency === 'string' && raw.currency) {
        shopContext.currency = raw.currency;
      }

      /** @type {{ email?: string; phone_number?: string }} */
      const user_data = {};

      if (raw.email) {
        const hashedEmail = await sha256Hex(raw.email);
        if (hashedEmail) user_data.email = hashedEmail;
      }

      if (raw.phone_number) {
        const phoneDigits = raw.phone_number.replace(/\D/g, '');
        if (phoneDigits) {
          const hashedPhone = await sha256Hex(phoneDigits);
          if (hashedPhone) user_data.phone_number = hashedPhone;
        }
      }

      win.__datalayerUserData = user_data;

      return {
        customer_type: raw.customer_type || 'Misafir',
        customer_id: raw.customer_id || '',
        user_data,
      };
    } catch (error) {
      console.error(error);
      return {
        customer_type: 'Misafir',
        customer_id: '',
        user_data: {},
      };
    }
  }

  function preloadCustomerContext() {
    if (!customerContextPromise) {
      customerContextPromise = loadCustomerContext();
    }
    return customerContextPromise;
  }

  function getCustomerContext() {
    return preloadCustomerContext();
  }

  function clearEcommerce() {
    dataLayer.push({ ecommerce: null });
  }

  /**
   * @param {Record<string, unknown>} payload
   * @param {CustomerContext} customer
   */
  function pushEvent(payload, customer) {
    clearEcommerce();

    /** @type {Record<string, unknown>} */
    const eventPayload = {
      ...payload,
      customer_type: customer.customer_type,
      customer_id: customer.customer_id,
    };

    if (customer.user_data.email || customer.user_data.phone_number) {
      eventPayload.user_data = customer.user_data;
    }

    dataLayer.push(eventPayload);
  }

  /**
   * @param {Record<string, unknown>} payload
   * @param {CustomerContext} customer
   * @returns {Promise<void>}
   */
  function pushEventAndWaitForGtm(payload, customer) {
    return new Promise((resolve) => {
      clearEcommerce();

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      /** @type {Record<string, unknown>} */
      const eventPayload = {
        ...payload,
        customer_type: customer.customer_type,
        customer_id: customer.customer_id,
        eventCallback: finish,
        eventTimeout: 2000,
      };

      if (customer.user_data.email || customer.user_data.phone_number) {
        eventPayload.user_data = customer.user_data;
      }

      dataLayer.push(eventPayload);
      window.setTimeout(finish, 2000);
    });
  }

  const SEARCH_DEDUPE_STORAGE_KEY = 'datalayer_search_sent';

  /**
   * @param {string} term
   * @returns {boolean}
   */
  function shouldSkipSearchPush(term) {
    const normalized = term.trim().toLowerCase();
    if (!normalized) return true;

    const raw = sessionStorage.getItem(SEARCH_DEDUPE_STORAGE_KEY);
    if (!raw) return false;

    try {
      /** @type {{ t?: string; at?: number }} */
      const parsed = JSON.parse(raw);
      if (parsed.t !== normalized) return false;
      if (typeof parsed.at !== 'number') return false;
      return Date.now() - parsed.at < 5000;
    } catch {
      return false;
    }
  }

  /**
   * @param {string} term
   */
  function markSearchPush(term) {
    const normalized = term.trim().toLowerCase();
    if (!normalized) return;

    sessionStorage.setItem(
      SEARCH_DEDUPE_STORAGE_KEY,
      JSON.stringify({
        t: normalized,
        at: Date.now(),
      })
    );
  }

  /**
   * @param {string} searchTerm
   * @param {CustomerContext} customer
   */
  function pushSearch(searchTerm, customer) {
    pushEvent(
      {
        event: 'search',
        search_term: searchTerm,
      },
      customer
    );
  }

  function initSearch() {
    const node = document.getElementById('datalayer-search');
    if (!(node instanceof HTMLScriptElement) || !node.textContent) return;
    if (node.dataset.datalayerSearchSent === 'true') return;

    try {
      /** @type {{ search_term?: string }} */
      const parsed = JSON.parse(node.textContent);
      const term = typeof parsed.search_term === 'string' ? parsed.search_term.trim() : '';
      if (!term) return;

      node.dataset.datalayerSearchSent = 'true';

      if (shouldSkipSearchPush(term)) return;

      markSearchPush(term);

      getCustomerContext().then((customer) => {
        pushSearch(term, customer);
      });
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {string}
   */
  function getSearchTermFromForm(form) {
    const input = form.querySelector('input[name="q"]');
    if (!(input instanceof HTMLInputElement)) return '';
    return input.value.trim();
  }

  /**
   * @param {string} term
   * @param {HTMLFormElement} form
   * @returns {string}
   */
  function buildSearchResultsUrl(term, form) {
    let pathname = '/search';
    const action = form.getAttribute('action');
    if (action) {
      try {
        pathname = new URL(action, window.location.origin).pathname;
      } catch {
        pathname = action.split('?')[0] || pathname;
      }
    }

    const url = new URL(pathname, window.location.origin);
    url.searchParams.set('q', term);
    return url.toString();
  }

  /**
   * @param {Event} event
   */
  async function handleSearchFormSubmit(event) {
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;

    const term = getSearchTermFromForm(form);
    if (!term) return;

    event.preventDefault();

    if (!shouldSkipSearchPush(term)) {
      try {
        const customer = await getCustomerContext();
        await pushEventAndWaitForGtm(
          {
            event: 'search',
            search_term: term,
          },
          customer
        );
        markSearchPush(term);
      } catch (error) {
        console.error(error);
      }
    }

    window.location.assign(buildSearchResultsUrl(term, form));
  }

  function bindSearchForms() {
    document.querySelectorAll('form').forEach((form) => {
      if (!(form instanceof HTMLFormElement)) return;
      if (form.dataset.datalayerSearchBound === 'true') return;

      const action = form.getAttribute('action') || '';
      const hasQueryInput = form.querySelector('input[name="q"]') instanceof HTMLInputElement;
      if (!hasQueryInput) return;
      if (action && !action.includes('/search')) return;

      form.dataset.datalayerSearchBound = 'true';
      form.addEventListener('submit', handleSearchFormSubmit);
    });
  }

  /**
   * @param {string} listId
   * @param {string} listName
   * @param {Record<string, unknown>} item
   * @param {CustomerContext} customer
   */
  function pushSelectItem(listId, listName, item, customer) {
    pushEvent(
      {
        event: 'select_item',
        ecommerce: {
          item_list_id: listId,
          item_list_name: listName,
          items: [item],
        },
      },
      customer
    );
  }

  /**
   * @param {{ currency: string; value: number; item: Record<string, unknown> }} viewItemData
   * @param {CustomerContext} customer
   */
  function pushViewItem(viewItemData, customer) {
    pushEvent(
      {
        event: 'view_item',
        ecommerce: {
          currency: viewItemData.currency,
          value: viewItemData.value,
          items: [viewItemData.item],
        },
      },
      customer
    );
  }

  /**
   * @param {string} listId
   * @param {string} listName
   * @param {Record<string, unknown>[]} items
   * @param {CustomerContext} customer
   */
  function pushViewItemList(listId, listName, items, customer) {
    pushEvent(
      {
        event: 'view_item_list',
        ecommerce: {
          item_list_id: listId,
          item_list_name: listName,
          items,
        },
      },
      customer
    );
  }

  function initViewItemList() {
    const node = document.getElementById('datalayer-view-item-list');
    if (!(node instanceof HTMLScriptElement) || !node.textContent) return;
    if (node.dataset.datalayerViewItemListSent === 'true') return;

    try {
      /** @type {{ item_list_id?: string; item_list_name?: string; items?: Record<string, unknown>[] }} */
      const parsed = JSON.parse(node.textContent);

      const listId = parsed.item_list_id;
      const listName = parsed.item_list_name;
      const items = parsed.items;

      if (typeof listId !== 'string' || typeof listName !== 'string' || !Array.isArray(items) || items.length === 0) {
        return;
      }

      node.dataset.datalayerViewItemListSent = 'true';

      getCustomerContext().then((customer) => {
        pushViewItemList(listId, listName, items, customer);
      });
    } catch (error) {
      console.error(error);
    }
  }

  function initViewItem() {
    const node = document.getElementById('datalayer-view-item');
    if (!(node instanceof HTMLScriptElement) || !node.textContent) return;
    if (node.dataset.datalayerViewItemSent === 'true') return;

    try {
      /** @type {{ currency?: string; value?: number; item?: Record<string, unknown> }} */
      const parsed = JSON.parse(node.textContent);

      const currency = parsed.currency;
      const item = parsed.item;

      if (typeof currency !== 'string' || !item || typeof item !== 'object') return;

      node.dataset.datalayerViewItemSent = 'true';

      const value = typeof parsed.value === 'number' ? parsed.value : 0;

      getCustomerContext().then((customer) => {
        pushViewItem(
          {
            currency,
            value,
            item,
          },
          customer
        );
      });
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * @param {MouseEvent} event
   */
  function handleSelectItemClick(event) {
    const target = event.currentTarget;
    if (!(target instanceof HTMLAnchorElement)) return;

    const card = target.closest('[data-datalayer-list-id]');
    if (!(card instanceof HTMLElement)) return;

    const listId = card.dataset.datalayerListId || '';
    const listName = card.dataset.datalayerListName || '';
    const itemRaw = target.dataset.datalayerItem;

    if (!listId || !itemRaw) return;

    try {
      const item = JSON.parse(itemRaw);
      getCustomerContext().then((customer) => {
        pushSelectItem(listId, listName, item, customer);
      });
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * @param {unknown} id
   * @returns {string}
   */
  function normalizeVariantId(id) {
    if (typeof id !== 'string' && typeof id !== 'number') return '';
    const raw = String(id).trim();
    if (!raw) return '';

    const gidMatch = raw.match(/ProductVariant\/(\d+)/i);
    if (gidMatch?.[1]) return gidMatch[1];

    return raw;
  }

  /**
   * @param {Record<string, unknown>} cart
   * @returns {string}
   */
  function extractCartCoupon(cart) {
    const apps = cart.cart_level_discount_applications;
    if (!Array.isArray(apps) || apps.length === 0) return '';

    /** @type {{ type?: string; title?: string } | undefined} */
    const codeDiscount = apps.find((app) => {
      if (typeof app !== 'object' || app === null) return false;
      return app.type === 'discount_code';
    });

    const first = apps[0];
    const title = codeDiscount?.title ?? (typeof first === 'object' && first !== null && 'title' in first ? first.title : '');
    return typeof title === 'string' ? title : '';
  }

  /**
   * @returns {Promise<{ items: Record<string, unknown>[]; currency: string; value: number; coupon: string }>}
   */
  async function fetchAjaxCart() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status}`);
    }

    /** @type {{ items?: Record<string, unknown>[]; currency?: string; total_price?: number; cart_level_discount_applications?: unknown[] }} */
    const cart = await response.json();
    const currency = typeof cart.currency === 'string' ? cart.currency : shopContext.currency;
    const value = Number(cart.total_price ?? 0) / 100;

    return {
      items: Array.isArray(cart.items) ? cart.items : [],
      currency,
      value,
      coupon: extractCartCoupon(cart),
    };
  }

  /**
   * @param {Record<string, unknown>[]} cartItems
   * @param {unknown} merchandiseId
   * @returns {Record<string, unknown> | undefined}
   */
  function findCartItemForMerchandise(cartItems, merchandiseId) {
    const targetId = normalizeVariantId(merchandiseId);
    if (!targetId) return undefined;

    return cartItems.find((item) => {
      if (typeof item !== 'object' || item === null) return false;
      return normalizeVariantId(item.variant_id) === targetId;
    });
  }

  /**
   * @param {Record<string, unknown>[]} cartItems
   * @param {unknown} lineKey
   * @returns {Record<string, unknown> | undefined}
   */
  function findCartItemByLineKey(cartItems, lineKey) {
    if (lineKey === undefined || lineKey === null || lineKey === '') return undefined;
    const targetKey = String(lineKey);

    return cartItems.find((item) => {
      if (typeof item !== 'object' || item === null) return false;
      return String(item.key) === targetKey;
    });
  }

  function bindSelectItemLinks() {
    document.querySelectorAll('[data-datalayer-product-link]').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;
      if (link.dataset.datalayerSelectItemBound === 'true') return;
      link.dataset.datalayerSelectItemBound = 'true';
      link.addEventListener('click', handleSelectItemClick);
    });
  }

  /**
   * @param {Record<string, unknown>} cartItem
   * @param {number} quantityAdded
   * @param {number} index
   * @returns {Record<string, unknown>}
   */
  function mapCartLineToGa4Item(cartItem, quantityAdded, index) {
    const priceCents = Number(cartItem.final_price ?? cartItem.price ?? 0);
    const originalCents = Number(cartItem.original_price ?? cartItem.price ?? priceCents);
    const unitPrice = priceCents / 100;
    const discountPerUnit = Math.max(0, (originalCents - priceCents) / 100);

    let variantTitle = '';
    if (typeof cartItem.variant_title === 'string' && cartItem.variant_title !== 'Default Title') {
      variantTitle = cartItem.variant_title;
    }

    const sku = cartItem.sku;
    const variantId = cartItem.variant_id;

    return {
      item_id: typeof sku === 'string' && sku ? sku : String(variantId ?? ''),
      item_name: cartItem.product_title ?? cartItem.title ?? '',
      affiliation: shopContext.shop_name,
      coupon: '',
      discount: Math.round(discountPerUnit * quantityAdded * 100) / 100,
      index,
      item_brand: cartItem.vendor ?? '',
      item_category: cartItem.product_type ?? '',
      item_category2: '',
      item_category3: '',
      item_category4: '',
      item_category5: '',
      item_list_id: '',
      item_list_name: '',
      item_variant: variantTitle,
      location_id: '',
      price: unitPrice,
      quantity: quantityAdded,
    };
  }

  /**
   * @param {string} currency
   * @param {number} value
   * @param {Record<string, unknown>[]} items
   * @param {CustomerContext} customer
   */
  function pushAddToCart(currency, value, items, customer) {
    pushEvent(
      {
        event: 'add_to_cart',
        ecommerce: {
          currency,
          value: Math.round(value * 100) / 100,
          items,
        },
      },
      customer
    );
  }

  /**
   * @param {string} currency
   * @param {number} value
   * @param {Record<string, unknown>[]} items
   * @param {CustomerContext} customer
   */
  function pushRemoveFromCart(currency, value, items, customer) {
    pushEvent(
      {
        event: 'remove_from_cart',
        ecommerce: {
          currency,
          value: Math.round(value * 100) / 100,
          items,
        },
      },
      customer
    );
  }

  /**
   * @param {string} currency
   * @param {number} value
   * @param {Record<string, unknown>[]} items
   * @param {CustomerContext} customer
   */
  function pushViewCart(currency, value, items, customer) {
    pushEvent(
      {
        event: 'view_cart',
        ecommerce: {
          currency,
          value: Math.round(value * 100) / 100,
          items,
        },
      },
      customer
    );
  }

  /**
   * @param {string} currency
   * @param {number} value
   * @param {Record<string, unknown>[]} items
   * @param {CustomerContext} customer
   * @param {string} coupon
   */
  /**
   * @param {string} currency
   * @param {number} value
   * @param {Record<string, unknown>[]} items
   * @param {CustomerContext} customer
   * @param {string} coupon
   * @returns {Promise<void>}
   */
  function pushBeginCheckout(currency, value, items, customer, coupon) {
    return pushEventAndWaitForGtm(
      {
        event: 'begin_checkout',
        ecommerce: {
          currency,
          value: Math.round(value * 100) / 100,
          coupon: coupon || '',
          items,
        },
      },
      customer
    );
  }

  /**
   * @param {Record<string, unknown>[]} cartItems
   * @returns {Record<string, unknown>[]}
   */
  function mapCartItemsToGa4List(cartItems) {
    return cartItems.map((cartItem, index) => {
      const lineQuantity = Number(cartItem.quantity) || 1;
      return mapCartLineToGa4Item(cartItem, lineQuantity, index);
    });
  }

  function pushViewCartFromAjax() {
    fetchAjaxCart()
      .then((cart) => {
        if (cart.currency) {
          shopContext.currency = cart.currency;
        }

        const gaItems = mapCartItemsToGa4List(cart.items);

        getCustomerContext().then((customer) => {
          pushViewCart(cart.currency, cart.value, gaItems, customer);
        });
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /** @type {boolean} */
  let beginCheckoutPushLock = false;

  /**
   * @returns {Promise<void>}
   */
  function pushBeginCheckoutFromAjax() {
    return fetchAjaxCart()
      .then(async (cart) => {
        if (!Array.isArray(cart.items) || cart.items.length === 0) return;

        if (cart.currency) {
          shopContext.currency = cart.currency;
        }

        const gaItems = mapCartItemsToGa4List(cart.items);
        const customer = await getCustomerContext();
        await pushBeginCheckout(cart.currency, cart.value, gaItems, customer, cart.coupon);
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * @returns {Promise<void>}
   */
  function triggerBeginCheckoutForNavigation() {
    if (beginCheckoutPushLock) return Promise.resolve();
    beginCheckoutPushLock = true;

    return pushBeginCheckoutFromAjax().finally(() => {
      window.setTimeout(() => {
        beginCheckoutPushLock = false;
      }, 1500);
    });
  }

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  function findAlternativeCheckoutTrigger(target) {
    if (!(target instanceof Element)) return null;

    if (target.closest('[data-lc-card-checkout]')) return null;

    const havale = target.closest('[data-havale-button], .havale-button');
    if (havale instanceof HTMLElement) return havale;

    const checkoutLink = target.closest('a[href="/checkout"], a[href^="/checkout?"]');
    if (checkoutLink instanceof HTMLAnchorElement) return checkoutLink;

    const namedCheckout = target.closest('[name="checkout"]');
    if (namedCheckout instanceof HTMLElement && !namedCheckout.hasAttribute('data-lc-card-checkout')) {
      return namedCheckout;
    }

    return null;
  }

  /**
   * @param {Event} event
   */
  async function handleAlternativeCheckoutClick(event) {
    const trigger = findAlternativeCheckoutTrigger(event.target);
    if (!trigger) return;
    if (trigger instanceof HTMLButtonElement && trigger.disabled) return;

    await triggerBeginCheckoutForNavigation();
  }

  function bindBeginCheckoutTriggers() {
    document.addEventListener('click', handleAlternativeCheckoutClick, true);
  }

  function isCartTemplatePage() {
    const main = document.getElementById('MainContent');
    return main?.dataset.template === 'cart';
  }

  function initViewCartPage() {
    if (!isCartTemplatePage()) return;

    const marker = document.getElementById('MainContent');
    if (!marker) return;
    if (marker.dataset.datalayerViewCartSent === 'true') return;

    marker.dataset.datalayerViewCartSent = 'true';
    pushViewCartFromAjax();
  }

  /**
   * @param {Event} event
   */
  function handleCartDrawerOpen(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.id !== 'cart-drawer') return;

    pushViewCartFromAjax();
  }

  function bindViewCartTriggers() {
    document.addEventListener(CART_DRAWER_OPEN_EVENT, handleCartDrawerOpen);
  }

  /**
   * @param {{ action?: string; lines?: { merchandiseId?: string | number; quantity?: number }[]; promise?: Promise<{ detail?: { didError?: boolean; items?: Record<string, unknown>[] }; cart?: { cost?: { totalAmount?: { currencyCode?: string } } } }> }} cartEvent
   */
  function handleCartAdd(cartEvent) {
    if (!cartEvent.promise) return;

    cartEvent.promise
      .then(async (result) => {
        /** @type {{ detail?: { didError?: boolean; items?: Record<string, unknown>[] }; cart?: { cost?: { totalAmount?: { currencyCode?: string } } } }} */
        const resolved = result ?? {};

        if (resolved.detail?.didError) return;

        const lines = cartEvent.lines;
        if (!Array.isArray(lines) || lines.length === 0) return;

        let cartItems = resolved.detail?.items;
        let currency = resolved.cart?.cost?.totalAmount?.currencyCode ?? shopContext.currency;

        if (!Array.isArray(cartItems) || cartItems.length === 0) {
          try {
            const ajaxCart = await fetchAjaxCart();
            cartItems = ajaxCart.items;
            currency = ajaxCart.currency;
          } catch (error) {
            console.error(error);
            return;
          }
        }

        /** @type {Record<string, unknown>[]} */
        const gaItems = [];
        let value = 0;

        lines.forEach((line, index) => {
          const quantityAdded = Number(line.quantity) || 1;
          const cartItem = findCartItemForMerchandise(cartItems, line.merchandiseId);
          if (!cartItem) return;

          const priceCents = Number(cartItem.final_price ?? cartItem.price ?? 0);
          value += (priceCents / 100) * quantityAdded;

          gaItems.push(mapCartLineToGa4Item(cartItem, quantityAdded, index));
        });

        if (gaItems.length === 0) return;

        if (currency) {
          shopContext.currency = currency;
        }

        getCustomerContext().then((customer) => {
          pushAddToCart(currency || shopContext.currency, value, gaItems, customer);
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.error(error);
        }
      });
  }

  /**
   * @param {{ action?: string; lines?: { id?: string; quantity?: number }[]; promise?: Promise<{ detail?: { didError?: boolean } }> }} cartEvent
   */
  function handleCartRemove(cartEvent) {
    if (!cartEvent.promise) return;

    const removeLines = cartEvent.lines;
    if (!Array.isArray(removeLines) || removeLines.length === 0) return;

    const snapshotPromise = fetchAjaxCart();

    cartEvent.promise
      .then(async (result) => {
        const resolved = result ?? {};
        if (resolved.detail?.didError) return;

        let cartSnapshot;
        try {
          cartSnapshot = await snapshotPromise;
        } catch (error) {
          console.error(error);
          return;
        }

        /** @type {Record<string, unknown>[]} */
        const gaItems = [];
        let value = 0;
        const currency = cartSnapshot.currency;

        removeLines.forEach((line, index) => {
          const cartItem = findCartItemByLineKey(cartSnapshot.items, line.id);
          if (!cartItem) return;

          const qtyRemoved = Number(cartItem.quantity) || 1;
          const priceCents = Number(cartItem.final_price ?? cartItem.price ?? 0);
          value += (priceCents / 100) * qtyRemoved;

          gaItems.push(mapCartLineToGa4Item(cartItem, qtyRemoved, index));
        });

        if (gaItems.length === 0) return;

        if (currency) {
          shopContext.currency = currency;
        }

        getCustomerContext().then((customer) => {
          pushRemoveFromCart(currency || shopContext.currency, value, gaItems, customer);
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.error(error);
        }
      });
  }

  /**
   * @param {Event} event
   */
  function handleCartLinesUpdate(event) {
    /** @type {{ action?: string; lines?: { merchandiseId?: string | number; id?: string; quantity?: number }[]; promise?: Promise<{ detail?: { didError?: boolean; items?: Record<string, unknown>[] } }> }} */
    const cartEvent = /** @type {typeof event & { action?: string; lines?: { merchandiseId?: string | number; id?: string; quantity?: number }[]; promise?: Promise<{ detail?: { didError?: boolean; items?: Record<string, unknown>[] } }> }} */ (
      event
    );

    if (!cartEvent.promise) return;

    if (cartEvent.action === 'add') {
      handleCartAdd(cartEvent);
      return;
    }

    if (cartEvent.action === 'remove') {
      handleCartRemove(cartEvent);
    }
  }

  function bindCartEvents() {
    document.addEventListener(CART_LINES_UPDATE_EVENT, handleCartLinesUpdate);
  }

  function bindDynamicContent() {
    bindSelectItemLinks();
    bindSearchForms();
  }

  win.__pushDatalayerBeginCheckout = triggerBeginCheckoutForNavigation;

  function initDataLayer() {
    preloadCustomerContext();
    bindDynamicContent();
    bindCartEvents();
    bindViewCartTriggers();
    bindBeginCheckoutTriggers();
    initViewItem();
    initViewItemList();
    initSearch();
    initViewCartPage();
    document.addEventListener('shopify:section:load', () => {
      bindDynamicContent();
      initViewItem();
      initViewItemList();
      initSearch();
      initViewCartPage();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDataLayer);
  } else {
    initDataLayer();
  }
})();
